import React, { useState, useEffect } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS, API_URLS } from "../../../../core/data/redux/apis/urls";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../../../utils/fileUtils";
import {
  formatDate,
  getOrdinalNumber,
} from "./common/nocUtils";
import { handleDocumentClick } from "./common/documentUtils";
import { NocHeader } from "./common/NocHeader";
import { NocEmployeeCard } from "./common/NocEmployeeCard";
import { NocWorkHistoryCard } from "./common/NocWorkHistoryCard";
import { NocDandARCard } from "./common/NocDandARCard";
import { NocChatSection } from "./common/NocChatSection";
import { NocActionModal } from "./common/NocActionModal";
import { DnarOption, RejectionReason, JobNatureOption, UploadedDoc } from "./common/types";

interface NocOutsideJobApprovalProps {
  id: string;
  nocData: any;
  onRefresh: () => void;
  onBack: () => void;
  dnarMasterList: DnarOption[];
  onShowToast: (title: string, message: string, type: "success" | "danger" | "warning" | "primary") => void;
}

export const NocOutsideJobApproval: React.FC<NocOutsideJobApprovalProps> = ({
  id,
  nocData,
  onRefresh,
  onBack,
  dnarMasterList,
  onShowToast,
}) => {
  // Local editable state
  const [natureOfOutside, setNatureOfOutside] = useState<string>("");
  const [postAppliedFor, setPostAppliedFor] = useState<string>("");
  const [organisationInWhich, setOrganisationInWhich] = useState<string>("");
  const [advertisementNo, setAdvertisementNo] = useState<string>("");
  const [advertisementDate, setAdvertisementDate] = useState<string>("");
  const [department, setDepartment] = useState<string>("");

  // D&AR Status
  const [dnARStatus, setDnARStatus] = useState<string>("");
  const [dnARPenaltyEffective, setDnARPenaltyEffective] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [finalDandARVigilance, setFinalDandARVigilance] = useState<string>("");

  // Approver Remarks (Remarks1)
  const [hRemarks, setHRemarks] = useState<string>("");

  // Contact Number
  const [contactNumber, setContactNumber] = useState<string>("");
  const [contactNumberError, setContactNumberError] = useState<string>("");

  // Rejection reasons & Job Nature options
  const [rejectionReasons, setRejectionReasons] = useState<RejectionReason[]>([]);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState<string>("");
  const [jobNatureOptions, setJobNatureOptions] = useState<JobNatureOption[]>([]);

  // HR document uploads
  const [hrDocType, setHrDocType] = useState<string>("O");
  const [hrUploadedDocs, setHrUploadedDocs] = useState<UploadedDoc[]>([]);
  const [hrDocUploading, setHrDocUploading] = useState<boolean>(false);

  // Employee Education Data for Level > 1 HR
  const [empEduData, setEmpEduData] = useState<any[]>([]);
  const [isEmpEduLoading, setIsEmpEduLoading] = useState<boolean>(false);

  // Action status
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<"A" | "R" | null>(null);

  // Field status helper (matching TaskDetailsPage)
  const getFieldStatus = (fieldName: string): "EDITABLE" | "READONLY" | "HIDDEN" => {
    const rawVal =
      nocData?.enabled_fields_fields_json?.[fieldName] ||
      nocData?.enabled_fields_json?.[fieldName] ||
      nocData?.[fieldName];

    if (rawVal === "X" || rawVal === "V" || rawVal === true) return "EDITABLE";
    return "READONLY";
  };

  // Sync initial values from nocData
  useEffect(() => {
    if (nocData) {
      setNatureOfOutside(nocData.natureDetails?.natureId || nocData.natureOfOutside || "");
      setPostAppliedFor(nocData.PostAppliedFor || nocData.postAppliedfor || "");
      setOrganisationInWhich(nocData.OrganisationInWhich || nocData.organisationInWhich || "");
      setAdvertisementNo(nocData.advertisementNo || "");
      setAdvertisementDate(nocData.advertisementDate || "");
      setDepartment(
        nocData?.department ||
        nocData?.dept ||
        nocData?.departmentName ||
        nocData?.employee?.details?.sap_department_designation_wings?.department_name ||
        ""
      );
      setDnARStatus(nocData?.DandARStatus || nocData?.dandARStatus || "");
      setDnARPenaltyEffective(nocData?.dandARPenaltyEffective || nocData?.DandARPenaltyEffective || "");
      setFinalDandARVigilance(nocData?.finalDandARVigilance || nocData?.FinalDandARVigilance || "");

      const isRemarksEditable =
        nocData?.enabled_fields_fields_json?.Remarks === "X" ||
        nocData?.enabled_fields_json?.Remarks === "X" ||
        nocData?.Remarks === "X";

      // If Remarks is editable for this approver, start fresh (empty) matching mobile app
      // If not editable, hold existing nocData.Remarks so it is preserved in payload
      const initialRemarks = isRemarksEditable
        ? ""
        : ((nocData?.Remarks !== "X" && nocData?.Remarks) ||
          (nocData?.remarks !== "X" && nocData?.remarks) ||
          "");
      setRemarks(initialRemarks);
      setHRemarks("");
      setContactNumber(
        nocData?.contactNumber ||
        nocData?.employee?.phone ||
        nocData?.employee?.details?.phone ||
        ""
      );
    }
  }, [nocData]);

  // Fetch Outside Job Nature Master
  useEffect(() => {
    const fetchJobNature = async () => {
      try {
        const res: any = await Api.post(API_URLS.NOC.OUTSIDE_JOB_NATURE, {});
        const raw = res?.data?.data?.data || res?.data?.data || res?.data;
        if (Array.isArray(raw)) {
          setJobNatureOptions(raw);
        }
      } catch (err) {
        console.warn("Failed to fetch outside job nature options:", err);
      }
    };
    fetchJobNature();
  }, []);

  // Fetch Rejection Reasons if enabled
  useEffect(() => {
    const isRejectionEnabled =
      nocData?.enabled_fields_fields_json?.ReasonForRejection === "X" ||
      nocData?.enabled_fields_json?.ReasonForRejection === "X";

    if (isRejectionEnabled) {
      const fetchReasons = async () => {
        try {
          const res: any = await Api.post(BASE_URLS.BASE_URL + "noc/noc-outjob-reject-reasons", {});
          const resData = res?.data?.data || res?.data;
          if (Array.isArray(resData) && resData.length > 0) {
            setRejectionReasons(resData);
          } else {
            setRejectionReasons([
              { id: 1, reasonForRejection: "1", description: "Incomplete details or documentation" },
              { id: 2, reasonForRejection: "2", description: "Not eligible as per DMRC policy" },
              { id: 3, reasonForRejection: "3", description: "Under D&AR/Vigilance proceedings" },
              { id: 4, reasonForRejection: "4", description: "Other administrative reason" },
            ]);
          }
        } catch (err) {
          setRejectionReasons([
            { id: 1, reasonForRejection: "1", description: "Incomplete details or documentation" },
            { id: 2, reasonForRejection: "2", description: "Not eligible as per DMRC policy" },
            { id: 3, reasonForRejection: "3", description: "Under D&AR/Vigilance proceedings" },
            { id: 4, reasonForRejection: "4", description: "Other administrative reason" },
          ]);
        }
      };
      fetchReasons();
    }
  }, [nocData]);

  // Fetch Employee Education Data for HR Approvers
  useEffect(() => {
    const targetEmpId = nocData?.employee?.empId || nocData?.empID || nocData?.empId;
    if (targetEmpId) {
      const fetchEdu = async () => {
        setIsEmpEduLoading(true);
        try {
          const res: any = await Api.post(API_URLS.NOC.GET_EMPLOYEE_EDUCATION, {
            empId: targetEmpId,
            data: "education",
          });
          const eduList = res?.data?.data?.education ?? res?.data?.data ?? [];
          setEmpEduData(Array.isArray(eduList) ? eduList : []);
        } catch (err) {
          console.warn("Failed fetching employee education data:", err);
          setEmpEduData([]);
        } finally {
          setIsEmpEduLoading(false);
        }
      };
      fetchEdu();
    }
  }, [nocData]);

  // HR Document Upload Handler
  const handleHrFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
      onShowToast("Error", "File type not allowed", "danger");
      return;
    }
    if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
      onShowToast("Error", "File size exceeds 2MB limit", "danger");
      return;
    }

    try {
      setHrDocUploading(true);
      const reader = new FileReader();
      reader.onload = async () => {
        const fullBase64 = reader.result as string;
        const base64String = fullBase64.includes(",") ? fullBase64.split(",")[1] : fullBase64;
        const res: any = await Api.post(`${BASE_URLS.BASE_URL}aws-bucket/upload-file`, {
          filestring: base64String,
          mime_type: file.type,
          module: "NOC_OUTJOB",
        });
        setHrDocUploading(false);
        if (res?.data?.data) {
          setHrUploadedDocs((prev) => [
            ...prev,
            {
              fileName: file.name,
              fileSize: `${Math.round(file.size / 1024)}KB`,
              serverKey: res.data.data.key,
              serverURL: res.data.data.url,
              documentType: hrDocType,
            },
          ]);
          onShowToast("Success", "Document uploaded successfully", "success");
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setHrDocUploading(false);
      onShowToast("Error", "Failed to upload document", "danger");
    }
  };

  const uploadOutjobDocuments = async () => {
    if (hrUploadedDocs.length === 0) {
      onShowToast("Warning", "Please upload at least one document", "warning");
      return;
    }
    try {
      const payload = {
        Action: "U",
        ReferenceNumber: nocData?.referenceNumber,
        documents: hrUploadedDocs.map((doc) => ({
          fileName: doc.serverKey,
          typeOfAttachedDoc: doc.documentType || "O",
        })),
      };
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}noc/outjob-approval-documents`, payload);
      if (res?.data) {
        onShowToast("Success", "Documents submitted successfully", "success");
        setHrUploadedDocs([]);
        onRefresh();
      }
    } catch (err: any) {
      onShowToast("Error", err?.message || "Failed to submit documents", "danger");
    }
  };

  // Approve / Reject Handler
  const handleApproveReject = async (action: "A" | "R") => {
    // 1. Validation for D&AR Status
    if (getFieldStatus("DandARStatus") === "EDITABLE") {
      if (!dnARStatus) {
        onShowToast("Error", "Please select D&AR status.", "danger");
        return;
      }
      if ((dnARStatus === "M" || dnARStatus === "J") && !dnARPenaltyEffective) {
        onShowToast("Error", "Please select D&AR Penalty Effective Date.", "danger");
        return;
      }
    }

    // 2. Validation for Contact Number if editable
    const isContactEditable =
      nocData?.enabled_fields_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_json?.ContactNumber === "X" ||
      nocData?.enabled_fields_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_json?.Phone === "X" ||
      nocData?.enabled_fields_fields_json?.contactNumber === "X" ||
      nocData?.enabled_fields_json?.contactNumber === "X";

    if (isContactEditable) {
      if (!contactNumber || !/^\d{10}$/.test(contactNumber)) {
        setContactNumberError("Contact number must be exactly 10 digits");
        onShowToast("Error", "Contact number must be exactly 10 digits.", "danger");
        return;
      }
    }

    // 3. Validation for Rejection Reason
    const isRejectionReasonEnabled =
      nocData?.enabled_fields_fields_json?.ReasonForRejection === "X" ||
      nocData?.enabled_fields_json?.ReasonForRejection === "X";

    if (action === "R" && isRejectionReasonEnabled && !selectedRejectionReason) {
      onShowToast("Error", "Please select a reason for rejection.", "danger");
      return;
    }

    // 3. Validation for Mandatory Remarks on Rejection
    if (action === "R" && !(hRemarks || "").trim()) {
      onShowToast("Error", "Approver remarks/comments are mandatory for rejection.", "danger");
      const remarksEl = document.getElementById("approver-remarks-textarea");
      if (remarksEl) {
        remarksEl.scrollIntoView({ behavior: "smooth", block: "center" });
        remarksEl.focus();
      }
      return;
    }

    // 4. Pending D&AR Warning
    if (action === "A" && (dnARStatus === "P" || nocData?.DandARStatus === "P" || nocData?.dandARStatus === "P")) {
      const confirmPending = window.confirm(
        "Application cannot be approved normally due to Pending D&AR. Are you sure you want to proceed?"
      );
      if (!confirmPending) return;
    }

    setIsProcessing(true);
    setProcessingAction(action);

    try {
      const isRemarksFieldEditable =
        nocData?.enabled_fields_fields_json?.Remarks === "X" ||
        nocData?.enabled_fields_json?.Remarks === "X" ||
        nocData?.Remarks === "X";

      const payload = {
        Approve_Reject_flag: action,
        ReferenceNumber: nocData.referenceNumber,
        remarks: (isRemarksFieldEditable ? (remarks || "") : (nocData?.Remarks || nocData?.remarks || remarks || "")).trim(),
        remarks1: (hRemarks || "").trim(),
        DandARStatus: dnARStatus || nocData?.DandARStatus || nocData?.dandARStatus || "",
        dandARPenaltyEffective: dnARPenaltyEffective || nocData?.dandARPenaltyEffective || nocData?.DandARPenaltyEffective || "",
        FinalDandARVigilance: finalDandARVigilance || nocData?.finalDandARVigilance || nocData?.FinalDandARVigilance || "",
        PostAppliedFor: postAppliedFor || nocData?.PostAppliedFor || nocData?.postAppliedfor || "",
        OrganisationInWhich: organisationInWhich || nocData?.OrganisationInWhich || nocData?.organisationInWhich || "",
        NatureOfOutside: natureOfOutside || nocData?.natureDetails?.natureId || nocData?.natureOfOutside || "",
        AdvertisementNo: advertisementNo || nocData?.advertisementNo || "",
        AdvertisementDate: advertisementDate || nocData?.advertisementDate || "",
        ReasonForRejection: action === "R" ? selectedRejectionReason || "" : "",
      };

      console.log("Outside Job Approval Payload:", payload);
      const res: any = await Api.post(`${BASE_URLS.BASE_URL}sap-master-worklist/approve_reject_action`, payload);

      if (res?.data) {
        const actionText = action === "A" ? "approved" : "rejected";
        onShowToast("Success", `Outside Job NOC application ${actionText} successfully`, "success");
        setTimeout(() => {
          onBack();
        }, 1500);
      }
    } catch (err: any) {
      console.error("Outside Job Approval Error:", err);
      onShowToast("Error", err?.message || "Action failed", "danger");
    } finally {
      setIsProcessing(false);
      setProcessingAction(null);
    }
  };

  const isHRUser = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw || raw === "undefined") return false;
      const parsed = JSON.parse(raw);
      const dept = (parsed?.data?.user || parsed?.user || parsed)?.details?.sap_department_designation_wings?.department_name || "";
      return dept.includes("HR") || dept.includes("HUMAN RESOURCE");
    } catch {
      return false;
    }
  })();

  const approverLevel = parseInt(nocData?.ApproverLevel || nocData?.approverLevel || "0");
  const isFinalDnarEditable = getFieldStatus("FinalDandARVigilance") === "EDITABLE";
  const isReasonForRejectionEnabled =
    nocData?.enabled_fields_fields_json?.ReasonForRejection === "X" ||
    nocData?.enabled_fields_json?.ReasonForRejection === "X";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <NocHeader
          title="NOC for Outside Job Approval"
          breadcrumbTitle="Outside Job Details"
          nocType="NOC_OUTJOB"
          nocData={nocData}
          onBack={onBack}
          onRefresh={onRefresh}
          onShowToast={onShowToast}
        />

        {/* Employee & Application Details Card */}
        <NocEmployeeCard
          nocData={nocData}
          nocType="NOC_OUTJOB"
          editableDepartment={department}
          onDepartmentChange={setDepartment}
          editableContactNumber={contactNumber}
          onContactNumberChange={(val) => {
            setContactNumber(val);
            if (/^\d{10}$/.test(val)) setContactNumberError("");
          }}
          contactNumberError={contactNumberError}
        />

        {/* Outside Job Details Card */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-briefcase me-2 text-primary"></i>
              Additional Outside Job Information
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Whether probation is confirmed in DMRC
                </label>
                <div className="fw-medium text-dark">
                  {nocData?.whetherProbation === "Y"
                    ? "Yes"
                    : nocData?.whetherProbation === "N"
                      ? "No"
                      : nocData?.whetherProbation === "0"
                        ? "Not Applicable"
                        : "-"}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Whether one year service after probation
                </label>
                <div className="fw-medium text-dark">
                  {nocData?.threeyearsRegularServ === "Y"
                    ? "Yes"
                    : nocData?.threeyearsRegularServ === "N"
                      ? "No"
                      : "-"}
                </div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Trained abroad by DMRC
                </label>
                <div className="fw-medium text-dark">
                  {nocData?.trainedAbroadByDM === "Y"
                    ? "Yes"
                    : nocData?.trainedAbroadByDM === "N"
                      ? "No"
                      : "N/A"}
                </div>
              </div>

              {nocData?.trainedAbroadByDM === "Y" && (
                <>
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                      Place of Training Abroad
                    </label>
                    <div className="fw-medium text-dark">{nocData?.placeAbroadTrain || "-"}</div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                      Year of Training
                    </label>
                    <div className="fw-medium text-dark">{nocData?.yearOfAbroadTrain || "-"}</div>
                  </div>
                </>
              )}

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  No. of Applications Forwarded in the Year
                </label>
                <div className="fw-medium text-dark">{getOrdinalNumber(nocData?.appNoInYear)}</div>
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Place of Posting
                </label>
                <div className="fw-medium text-dark">{nocData?.placeofPosting || "-"}</div>
              </div>

              {/* Nature of Outside Job */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Nature of Outside Job
                </label>
                {getFieldStatus("NatureOfOutside") === "EDITABLE" ? (
                  <select
                    className="form-select"
                    value={natureOfOutside}
                    onChange={(e) => setNatureOfOutside(e.target.value)}
                  >
                    <option value="">Select Nature...</option>
                    {jobNatureOptions.map((opt) => (
                      <option
                        key={opt.sap_master_natures_id || opt.id}
                        value={opt.sap_master_natures_natureId || opt.natureId}
                      >
                        {opt.sap_master_natures_natureTitle || opt.natureTitle}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="fw-medium text-dark">
                    {nocData?.natureDetails?.natureTitle || nocData?.natureOfOutside || "-"}
                  </div>
                )}
              </div>

              {/* Post Applied For */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Name of Post Applied For
                </label>
                {getFieldStatus("PostAppliedFor") === "EDITABLE" ? (
                  <input
                    type="text"
                    className="form-control"
                    value={postAppliedFor}
                    onChange={(e) => setPostAppliedFor(e.target.value)}
                  />
                ) : (
                  <div className="fw-medium text-dark">{postAppliedFor || "-"}</div>
                )}
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Newspaper / Website
                </label>
                <div className="fw-medium text-dark">{nocData?.newspaperWebsite || "-"}</div>
              </div>

              {/* Organization */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Name of Organization in Which Applied
                </label>
                {getFieldStatus("OrganisationInWhich") === "EDITABLE" ? (
                  <input
                    type="text"
                    className="form-control"
                    value={organisationInWhich}
                    onChange={(e) => setOrganisationInWhich(e.target.value)}
                  />
                ) : (
                  <div className="fw-medium text-dark">
                    {nocData?.OrganisationInWhich || nocData?.organisation || "-"}
                  </div>
                )}
              </div>

              {/* Advertisement No */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Advertisement No.
                </label>
                {getFieldStatus("AdvertisementNo") === "EDITABLE" ? (
                  <input
                    type="text"
                    className="form-control"
                    value={advertisementNo}
                    onChange={(e) => setAdvertisementNo(e.target.value)}
                  />
                ) : (
                  <div className="fw-medium text-dark">{advertisementNo || "-"}</div>
                )}
              </div>

              {/* Advertisement Date */}
              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Advertisement Date
                </label>
                {getFieldStatus("AdvertisementDate") === "EDITABLE" ? (
                  <DatePicker
                    className="form-control"
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    value={advertisementDate && dayjs(advertisementDate).isValid() ? dayjs(advertisementDate) : null}
                    onChange={(date) => setAdvertisementDate(date ? date.format("YYYY-MM-DD") : "")}
                  />
                ) : (
                  <div className="fw-medium text-dark">{formatDate(advertisementDate)}</div>
                )}
              </div>

              <div className="col-md-4 col-sm-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Last Date of Receipt of Application
                </label>
                <div className="fw-medium text-dark">{formatDate(nocData?.lastDateOfApplica)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Education Details Accordion/Section */}
        {Array.isArray(nocData?.education) && nocData.education.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-school me-2 text-primary"></i>
                Educational Qualifications Details
              </h5>
            </div>
            <div className="card-body">
              {nocData.education.map((item: any, idx: number) => (
                <div key={idx} className="p-3 bg-light rounded border mb-3">
                  <h6 className="fw-bold text-primary mb-3">Qualification {idx + 1}</h6>
                  <div className="row g-3">
                    <div className="col-md-4 col-sm-6">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Course Name</small>
                      <div className="fw-bold text-dark">{item?.courseName || "-"}</div>
                    </div>
                    <div className="col-md-4 col-sm-6">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Date of Admission</small>
                      <div className="fw-bold text-dark">{formatDate(item?.courseDateOfAdmission)}</div>
                    </div>
                    <div className="col-md-4 col-sm-6">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Date of Passing</small>
                      <div className="fw-bold text-dark">{formatDate(item?.courseDateOfPassing)}</div>
                    </div>
                    <div className="col-md-4 col-sm-6">
                      <small className="text-muted d-block uppercase fw-medium mb-1">When Acquired</small>
                      <div className="fw-bold text-dark">
                        {item?.qualificationAcquir === "A" ? "After Joining" : "Before Joining"}
                      </div>
                    </div>
                    <div className="col-md-4 col-sm-6">
                      <small className="text-muted d-block uppercase fw-medium mb-1">Affiliated by UGC/AICTE</small>
                      <div className="fw-bold text-dark">
                        {item?.isCourseRecog === "Y" ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>

                  {/* Attached edu_documents matching mobile ApplicationtoNOC.tsx */}
                  {(() => {
                    const rawDocs = [
                      ...(Array.isArray(item?.edu_documents) ? item.edu_documents : []),
                      ...(Array.isArray(nocData?.documents)
                        ? nocData.documents.filter(
                          (doc: any) =>
                            (item.id && doc.request_education_id === item.id) ||
                            (Array.isArray(nocData.education) &&
                              nocData.education.length === 1 &&
                              (doc.typeOfAttachedDoc === "N" ||
                                doc.typeOfAttachedDoc === "E" ||
                                doc.typeOfAttachedDoc === "noc" ||
                                doc.typeOfAttachedDoc === "education" ||
                                doc.typeOfAttachedDoc === "qualification" ||
                                doc.typeOfAttachedDoc === "NOC/Addition"))
                        )
                        : []),
                    ];

                    // Deduplicate by fileName & length < 20 (matching mobile ApplicationtoNOC.tsx)
                    const uniqueDocs = rawDocs
                      .filter((d: any) => !d.referenceNumber || d.referenceNumber.toString().length < 20)
                      .filter(
                        (d: any, i: number, self: any[]) =>
                          i === self.findIndex((t: any) => t.fileName === d.fileName)
                      );

                    // Only display Education/Qualification Certificates here (NOC or Addition Office Order is shown in Application Attachments)
                    const eduCertDocs = uniqueDocs.filter((doc: any) => {
                      const typeStr = (doc.typeOfAttachedDoc || doc.documentType || "").toString().toUpperCase();
                      const isNoc =
                        typeStr === "N" ||
                        typeStr.includes("NOC") ||
                        typeStr.includes("ADDITION") ||
                        (doc.fileName && doc.fileName.toLowerCase().includes("noc"));
                      return !isNoc;
                    });

                    if (eduCertDocs.length === 0) return null;

                    return (
                      <div className="mt-3 pt-3 border-top">
                        <div className="mb-3">
                          <small className="text-muted d-block fw-semibold mb-2">
                            <i className="ti ti-school text-primary me-1"></i>
                            Education/Qualification Certificates
                          </small>
                          <div className="d-flex flex-wrap gap-2">
                            {eduCertDocs.map((doc: any, dIdx: number) => (
                              <button
                                key={doc.id || dIdx}
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                                onClick={() => handleDocumentClick(doc, "NOC_OUTJOB", onShowToast)}
                                title="View Document"
                              >
                                <i className="ti ti-file-text me-1"></i>
                                {doc.fileName?.split("/").pop() || "Document"}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attachments Card matching user side outsideJobDetails.tsx table form (includes Advertisement and NOC/Addition Office Order) */}
        {(() => {
          const mainDocs = Array.isArray(nocData?.documents) ? nocData.documents : [];
          const eduDocs = Array.isArray(nocData?.education)
            ? nocData.education.flatMap((e: any) => (Array.isArray(e.edu_documents) ? e.edu_documents : []))
            : [];
          const allDocs = [...mainDocs, ...eduDocs];
          const appDocs = allDocs
            .filter((doc: any) => {
              const typeStr = (doc.typeOfAttachedDoc || doc.documentType || "").toString().toUpperCase();
              const isNoc = typeStr === "N" || typeStr.includes("NOC") || typeStr.includes("ADDITION");
              const isEduCert = !isNoc && (typeStr === "E" || typeStr === "QUALIFICATION" || typeStr === "EDUCATION" || typeStr.includes("QUAL"));
              // Exclude only qualification certificates (they remain in Educational Details section)
              if (isEduCert) return false;
              return true;
            })
            .filter(
              (item: any) => (!item.referenceNumber ? true : item.referenceNumber.toString().length < 20)
            )
            .filter(
              (item: any, index: number, self: any[]) =>
                index === self.findIndex((t: any) => (t.id && item.id ? t.id === item.id : t.fileName === item.fileName))
            );

          if (appDocs.length === 0) return null;

          return (
            <div className="card mb-4 border-0 shadow-sm">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <i className="ti ti-paperclip me-2 text-primary"></i>
                  Application Attachments ({appDocs.length})
                </h5>
              </div>
              <div className="card-body">
                {/* Mandatory Attachments Info Card */}
                <div className="alert alert-danger bg-danger-transparent border-danger mb-3 p-3">
                  <h6 className="text-danger mb-2">
                    <i className="ti ti-info-circle me-2"></i>
                    Mandatory Attachments
                  </h6>
                  <ul className="mb-0 ps-3 small text-muted">
                    <li>- Copy of advertisement indicating last date of application and post applied for must be attached.</li>
                    <li>- If, with the existing educational qualifications (as captured in SAP), the employee is not eligible for the post, uploading requisite educational qualification documents is mandatory, on basis of which the employee is eligible for the post that he/she intends to apply for.</li>
                    <li>- Documents to be uploaded - Final Marksheet/Certificate</li>
                  </ul>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">File Name</th>
                        <th scope="col">Document Type</th>
                        <th scope="col">Upload Date</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appDocs.map((doc: any, index: number) => {
                        const typeStr = (doc.typeOfAttachedDoc || doc.documentType || "").toString().toUpperCase();
                        const isNoc = typeStr === "N" || typeStr.includes("NOC") || typeStr.includes("ADDITION");
                        const isAdv = typeStr === "A" || typeStr.includes("ADV") || typeStr === "ADVERTISING";
                        const badgeClass = isNoc ? "bg-info" : isAdv ? "bg-dark" : "bg-primary";
                        const badgeLabel = isNoc
                          ? "NOC or Addition Office Order"
                          : isAdv
                            ? "Advertisement for Outside Job"
                            : doc.typeOfAttachedDoc || "Attachment";

                        return (
                          <tr key={doc.id || index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="ti ti-file-text text-primary me-2"></i>
                                {index + 1}
                              </div>
                            </td>
                            <td>
                              <div className="fw-medium text-dark">
                                {doc.fileName ? doc.fileName.split("/").pop() : `File ${index + 1}`}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${badgeClass} text-white`}>
                                {badgeLabel}
                              </span>
                            </td>
                            <td>
                              <span className="text-muted">
                                {formatDate(doc.created_at || doc.uploadedAt || nocData?.created_at)}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={() => handleDocumentClick(doc, "NOC_OUTJOB", onShowToast)}
                                title="View/Download File"
                                disabled={!doc.fileName && !doc.UUID && !doc.docID}
                              >
                                <i className="ti ti-eye"></i>
                                <span>View</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Employee Education Data for HR Approvers */}
        {isHRUser && approverLevel > 1 && empEduData.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0">
                <i className="ti ti-school me-2 text-primary"></i>
                Employee Education Record (HR Verification)
              </h5>
              {isEmpEduLoading && (
                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
              )}
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "50px" }} className="text-center">#</th>
                      <th>Education</th>
                      <th>Certificate</th>
                      <th>Course Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empEduData.map((item: any, idx: number) => (
                      <tr key={idx}>
                        <td className="text-center fw-medium">{idx + 1}</td>
                        <td>{item.education_text || "-"}</td>
                        <td>{item.courseName1 || "-"}</td>
                        <td>{item.courseName2 || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Other / Declaration */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-light">
            <h5 className="card-title mb-0">
              <i className="ti ti-info-circle me-2 text-primary"></i>
              Other Disciplinary & Declaration Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Whether any penalty imposed during last 10 years D&AR in any D&AR case
                </label>
                <div className="fw-medium text-dark">{nocData?.dandARPendingOrNot === "Y" ? "Yes" : nocData?.dandARPendingOrNot === "N" ? "No" : "-"}</div>
              </div>
              <div className="col-md-6">
                <label className="form-label text-muted mb-1 text-uppercase small fw-semibold">
                  Whether any penalty imposed during last 10 years Vigilance in any Vigilance case
                </label>
                <div className="fw-medium text-dark">{nocData?.vigilancePending === "Y" ? "Yes" : nocData?.vigilancePending === "N" ? "No" : "-"}</div>
              </div>
            </div>

            <div className="p-3 bg-light rounded border">
              <small className="text-muted d-block uppercase fw-medium mb-2">Declaration Checklist</small>
              <div className="d-flex align-items-center mb-2">
                <input type="checkbox" className="form-check-input mt-0 me-2" checked disabled />
                <span className="small text-dark">
                  I hereby declare that the information given above is true.
                </span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <input type="checkbox" className="form-check-input mt-0 me-2" checked disabled />
                <span className="small text-dark">
                  I declare that I am prima facie meeting the eligibility criteria (educational qualification, age, experience, pay scale, etc.) for the post I intend to apply for, as per the enclosed/uploaded advertisement.
                </span>
              </div>
              {(nocData?.natureOfOutsideJob === "Deputation" || nocData?.natureDetails?.natureTitle === "Deputation") && (
                <div className="d-flex align-items-center">
                  <input type="checkbox" className="form-check-input mt-0 me-2" checked disabled />
                  <span className="small text-dark">
                    I am aware about the terms and conditions of Deputation, as applicable to the employees of DMRC and I shall provide a copy of the same to the outside organization in which I intend to apply for employment, on deputation basis.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Work History / Previous Remarks */}
        <NocWorkHistoryCard nocData={nocData} />

        {/* D&AR Status Section */}
        <NocDandARCard
          nocData={nocData}
          nocType="NOC_OUTJOB"
          dnARStatus={dnARStatus}
          dnARPenaltyEffective={dnARPenaltyEffective}
          remarks={remarks}
          dnarMasterList={dnarMasterList}
          onStatusChange={setDnARStatus}
          onPenaltyEffectiveChange={setDnARPenaltyEffective}
          onRemarksChange={setRemarks}
          getFieldStatus={getFieldStatus}
        />

        {/* Final D&AR / Vigilance Status HR (Level 2+ / HR only) */}
        {isFinalDnarEditable && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-eye me-2 text-primary"></i>
                Final D&AR / Vigilance Status Checked by HR Department
              </h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label fw-bold">HR Final Status Remarks:</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter HR final status remarks..."
                  value={finalDandARVigilance}
                  onChange={(e) => setFinalDandARVigilance(e.target.value)}
                />
              </div>

              <div className="p-3 bg-light rounded border">
                <label className="form-label fw-bold mb-2">Upload Documents:</label>
                <div className="row g-2 align-items-center mb-3">
                  <div className="col-md-5">
                    <select
                      className="form-select form-select-sm"
                      value={hrDocType}
                      onChange={(e) => setHrDocType(e.target.value)}
                    >
                      <option value="E">Education / Qualification Certificates</option>
                      <option value="A">Advertisement for Outside Job</option>
                      <option value="O">Other Documents</option>
                      <option value="N">NOC or Additional Office Order</option>
                    </select>
                  </div>
                  <div className="col-md-5">
                    <input
                      type="file"
                      className="form-control form-control-sm"
                      onChange={handleHrFileUpload}
                      disabled={hrDocUploading}
                    />
                  </div>
                  <div className="col-md-2">
                    {hrDocUploading && (
                      <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                    )}
                  </div>
                </div>

                {/* Uploaded docs list */}
                {hrUploadedDocs.length > 0 && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-semibold mb-1">Ready for submission:</small>
                    <div className="d-flex flex-wrap gap-2">
                      {hrUploadedDocs.map((doc, idx) => (
                        <div key={idx} className="badge bg-white text-dark border p-2 d-flex align-items-center gap-2">
                          <i className="ti ti-file-text text-primary"></i>
                          <span>{doc.fileName} ({doc.fileSize})</span>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-danger p-0 rounded-circle"
                            style={{ width: 18, height: 18 }}
                            onClick={() => setHrUploadedDocs((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* <button
                      type="button"
                      className="btn btn-sm btn-primary mt-2"
                      onClick={uploadOutjobDocuments}
                    >
                      <i className="ti ti-upload me-1"></i>
                      Confirm & Submit Documents
                    </button> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reason for Rejection Dropdown */}
        {isReasonForRejectionEnabled && rejectionReasons.length > 0 && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h5 className="card-title mb-0">
                <i className="ti ti-alert-triangle me-2 text-danger"></i>
                Reason for Rejection
              </h5>
            </div>
            <div className="card-body">
              <label className="form-label fw-bold">
                Select Reason for Rejection <span className="text-danger">*</span>
                <span className="text-muted fw-normal small ms-2">(Mandatory if rejecting)</span>
              </label>
              <select
                className="form-select"
                value={selectedRejectionReason}
                onChange={(e) => setSelectedRejectionReason(e.target.value)}
              >
                <option value="">Select Reason...</option>
                {rejectionReasons.map((r) => (
                  <option key={r.id || r.reasonForRejection} value={r.reasonForRejection || r.id}>
                    {r.description || r.reasonForRejection}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Communication & Chat Section */}
        <NocChatSection
          nocData={nocData}
          nocType="NOC_OUTJOB"
          onShowToast={onShowToast}
        />

        {/* Approve / Reject Actions Modal & Buttons */}
        <NocActionModal
          hRemarks={hRemarks}
          onHRemarksChange={setHRemarks}
          onApproveReject={handleApproveReject}
          isProcessing={isProcessing}
          processingAction={processingAction}
          showRemarksSection={true}
          remarksRequiredForReject={true}
        />
      </div>
    </div>
  );
};
